import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/config";
import { Plus, Edit2, Trash2, X, Save, Upload } from "lucide-react";

const EMPTY = {
  client_name: "",
  logo: "",
  image: "",
  headline: "",
  body: "",
  order: 0,
  published: true,
};

function formatErr(d) {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function AdminReferences() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(null); // "logo" | "image" | null
  const logoRef = useRef(null);
  const imageRef = useRef(null);

  const load = () => api.get("/admin/references").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onNew = () => { setEditing("new"); setForm({ ...EMPTY, order: items.length + 1 }); setErr(""); };
  const onEdit = (r) => { setEditing(r.id); setForm({ ...EMPTY, ...r }); setErr(""); };

  const onUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      setForm(f => ({ ...f, [field]: fullUrl }));
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally {
      setUploading(null);
      if (logoRef.current) logoRef.current.value = "";
      if (imageRef.current) imageRef.current.value = "";
    }
  };

  const onSave = async () => {
    setErr("");
    try {
      if (editing === "new") await api.post("/admin/references", form);
      else await api.put(`/admin/references/${editing}`, form);
      setEditing(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette référence ?")) return;
    await api.delete(`/admin/references/${id}`); load();
  };

  const move = async (r, delta) => {
    await api.put(`/admin/references/${r.id}`, { order: r.order + delta });
    load();
  };

  return (
    <div data-testid="admin-references">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="section-num">Références</p>
          <h1 className="display text-4xl mt-3">Études de cas clients</h1>
          <p className="text-ink/70 mt-2 text-sm max-w-xl">
            Chaque référence : 1 logo client, 1 photo de contexte, 1 titre court, 1 texte descriptif.
            Affichées sur la page publique <code>/references</code>.
          </p>
        </div>
        <button onClick={onNew} className="btn-pill btn-primary" data-testid="admin-new-reference">
          <Plus size={16}/> Ajouter une référence
        </button>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map(r => (
          <div key={r.id} className="bg-paper border border-ink/10 overflow-hidden" data-testid={`admin-ref-${r.id}`}>
            <div className="relative h-44 bg-paper-2">
              {r.image
                ? <img src={r.image} alt="" className="absolute inset-0 w-full h-full object-cover"/>
                : <div className="absolute inset-0 flex items-center justify-center text-ink/40 italic text-sm">Aucune photo</div>}
              {r.logo && (
                <div className="absolute top-3 left-3 bg-brand-deep px-3 py-2 rounded">
                  <img src={r.logo} alt={r.client_name} className="h-7 max-w-[140px] object-contain opacity-90"/>
                </div>
              )}
            </div>
            <div className="p-5">
              <p className="text-[11px] uppercase tracking-wider text-brand">{r.client_name}</p>
              <p className="display text-xl leading-tight mt-1">{r.headline || "(Sans titre)"}</p>
              <p className="text-sm text-ink/75 mt-2 line-clamp-2">{r.body}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-ink/10">
                <p className="text-[12px] text-ink/60">Ordre {r.order} · {r.published ? "Publié" : "Caché"}</p>
                <div className="flex gap-1">
                  <button onClick={() => move(r, -1)} className="p-2 hover:bg-ink/5" title="Monter">↑</button>
                  <button onClick={() => move(r, 1)} className="p-2 hover:bg-ink/5" title="Descendre">↓</button>
                  <button onClick={() => onEdit(r)} className="p-2 hover:bg-ink/5"><Edit2 size={14}/></button>
                  <button onClick={() => onDelete(r.id)} className="p-2 hover:bg-paper-2 text-accent-pink"><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-paper max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 border-b border-ink/10 flex items-center justify-between">
              <h2 className="display text-2xl">{editing === "new" ? "Nouvelle référence" : "Modifier la référence"}</h2>
              <button onClick={() => setEditing(null)}><X size={22}/></button>
            </div>
            <div className="p-8 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/70">Nom du client *</label>
                <input className="field" value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} placeholder="ex : Airbus"/>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-ink/70">Titre court</label>
                <input className="field" value={form.headline} onChange={e => setForm(f => ({...f, headline: e.target.value}))} placeholder="ex : Accompagnement CODIR sur 12 mois"/>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-ink/70">Descriptif</label>
                <textarea rows={5} className="field" value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))} placeholder="Contexte de la mission, périmètre, résultats…"/>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-ink/70">Logo client</label>
                  {form.logo && (
                    <div className="mt-3 bg-brand-deep p-4 rounded inline-block">
                      <img src={form.logo} alt="" className="max-h-14 opacity-90"/>
                    </div>
                  )}
                  <div className="flex gap-3 mt-3">
                    <input ref={logoRef} type="file" accept="image/*" onChange={(e) => onUpload(e, "logo")} className="hidden"/>
                    <button type="button" onClick={() => logoRef.current?.click()} disabled={uploading === "logo"} className="btn-pill btn-ghost disabled:opacity-60">
                      {uploading === "logo" ? "Envoi…" : <><Upload size={14}/> {form.logo ? "Remplacer le logo" : "Uploader un logo"}</>}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-ink/70">Photo de contexte</label>
                  {form.image && (
                    <div className="mt-3 rounded overflow-hidden inline-block">
                      <img src={form.image} alt="" className="max-h-24 max-w-xs object-cover"/>
                    </div>
                  )}
                  <div className="flex gap-3 mt-3">
                    <input ref={imageRef} type="file" accept="image/*" onChange={(e) => onUpload(e, "image")} className="hidden"/>
                    <button type="button" onClick={() => imageRef.current?.click()} disabled={uploading === "image"} className="btn-pill btn-ghost disabled:opacity-60">
                      {uploading === "image" ? "Envoi…" : <><Upload size={14}/> {form.image ? "Remplacer la photo" : "Uploader une photo"}</>}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-wider text-ink/70">Ordre d'affichage</label>
                  <input type="number" className="field" value={form.order} onChange={e => setForm(f => ({...f, order: parseInt(e.target.value) || 0}))}/>
                </div>
                <label className="flex items-center gap-3 pt-7">
                  <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({...f, published: e.target.checked}))}/>
                  <span className="text-sm">Publié sur la page Références</span>
                </label>
              </div>

              {err && <p className="text-accent-pink text-sm">{err}</p>}
              <div className="flex gap-3 pt-4">
                <button onClick={onSave} className="btn-pill btn-primary" data-testid="admin-ref-save"><Save size={16}/> Enregistrer</button>
                <button onClick={() => setEditing(null)} className="btn-pill btn-ghost">Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
